import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { DataGrid } from "@mui/x-data-grid";
import dayjs from "dayjs";
import {
  DATA_TAG_DUPLICATE_DRIVE_DATA,
  FETCH_DATA_TAG_USERS,
} from "../DataTagConstants";
import Page_Loader from "../../../assets/vizr_images/page_loader.gif";
import "./DataTagAllFilesTable.scss";
import FilterableHeader from "../Data Tag FIlters/FilterableHeader";
import DataTagUserSearch from "../Data Tag FIlters/DataTagUserSearch";
import SensitivityBadge from "../../../components/shared/Badge/SensitivityBadge";
import DataTagSensitivityFilter from "../Data Tag FIlters/DataTagSensitivityFilter";
import { sizeToBytes } from "../DataTagUtilities";
import DuplicateUserDrillDown from "./DuplicateUserDrillDown";
import DriveDrawer from "./DriveDrawer";

const safe = (value) =>
  value === null || value === undefined || value === "" ? "-" : value;

export const renderDataTagNames = (users, fileNameExpanded) => {
  if (!users || users.length === 0) return "-";

  const formattedUser = /^\d+$/.test(users)
    ? users
    : users
        .toLowerCase()
        .split(" ")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");

  return (
    <div
      className={`dataEstateUsersTable ${fileNameExpanded ? "expanded" : ""}`}
      title={formattedUser}
    >
      {formattedUser}
    </div>
  );
};

export const renderDuplicateCounts = (count) => {
  if (count === 0) {
    return (
      <span title="Unique File">
        <img src="/DataTagIcons/file-unique.svg" alt="Unique File" style={{width: "30px"}}/>
      </span>
    );
  } else if (count > 0) {
    return (
      <span title={`Original File\nNo of Duplicates: ${count}`} >
        <img src="/DataTagIcons/file-original.svg" alt="Original File" style={{width: "30px"}}/>
      </span>
    );
  } else {
    return (
      <span title="Duplicate File">
        <img src="/DataTagIcons/file-duplicate.svg" alt="Duplicate File" style={{width: "30px"}}/>
      </span>
    );
  }
};

export default function DataTagUsersTable() {
  const dispatch = useDispatch();
  const [selectedSensitivity, setSelectedSensitivity] = useState([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [activeDrive, setActiveDrive] = useState(null);
  const isDataBase = useSelector((state) => state.dataTagReducer.isDatabase);

  const dataSourceId = useSelector(
    (state) => state.dataTagReducer.selectedDataSource?.id,
  );

  const tableData = useSelector(
    (state) => state.dataTagReducer.users?.content || [],
  );

  const totalCount = useSelector(
    (state) => state.dataTagReducer.users?.totalCount || 0,
  );

  const duplicateDriveData = useSelector(
    (state) => state.dataTagReducer.duplicateDriveData || [],
  );

  const [usernameFilter, setUsernameFilter] = useState([]);

  const [paginationModel, setPaginationModel] = useState({
    pageSize: 100,
    page: 0,
  });

  useEffect(() => {
    if (dataSourceId) {
      dispatch({
        type: FETCH_DATA_TAG_USERS,
        payload: {
          dataSourceId,
          page: paginationModel.page,
          size: paginationModel.pageSize,
          usernames: usernameFilter.length > 0 ? usernameFilter : null,
          sensitivity:
            selectedSensitivity.length > 0 ? selectedSensitivity : null,
        },
      });
      dispatch({
        type: DATA_TAG_DUPLICATE_DRIVE_DATA,
        payload: {
          dataSourceId,
          page: paginationModel.page,
          size: paginationModel.pageSize,
        },
      });
    }
  }, [
    dataSourceId,
    paginationModel.page,
    paginationModel.pageSize,
    usernameFilter,
    selectedSensitivity,
  ]);

  const dupLookup = useMemo(() => {
    return duplicateDriveData.reduce((map, entry) => {
      if (entry?.actor?.id) {
        map[entry.actor.id] = {
          mutationCount: entry.mutationCount,
          filesDuplicated: entry.filesDuplicated,
        };
      }
      return map;
    }, {});
  }, [duplicateDriveData]);

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
    setActiveDrive(null);
  };

  const handleSelect = (user) => {
    setActiveDrive({
      enterpriseActorId: user.userId,
      driveName: user.userName,
      email: user.userEmail,
      ...user,
    });

    setDrawerOpen(true);
  };

  const formatTime = (value) =>
    value
      ? `${dayjs(value).format("MM-DD-YYYY")} ${dayjs(value).format("HH:mm")}`
      : "-";

  const handlePaginationChange = (params) => {
    const newPagination = {
      pageSize: params.pageSize,
      page: params.page,
    };
    if (paginationModel.pageSize !== newPagination.pageSize) {
      newPagination.page = 0;
    }
    setPaginationModel(newPagination);
  };

  const columns = [
    {
      field: "userName",
      headerName: "User Name",
      flex: 1.5,
      renderHeader: () => (
        <FilterableHeader
          title="User Name"
          isFilterApplied={usernameFilter.length > 0}
          onClear={() => setUsernameFilter([])}
        >
          <DataTagUserSearch
            selectedValues={usernameFilter}
            onSelectionChange={(selected) => setUsernameFilter(selected)}
          />
        </FilterableHeader>
      ),
      renderCell: (p) => renderDataTagNames(p.value),
    },
    {
      field: "userEmail",
      headerName: "Email",
      flex: 2,
      renderCell: (p) => renderDataTagNames(p.value),
    },
    {
      field: "userRole",
      headerName: "Role",
      flex: 1.5,
      renderCell: (p) => renderDataTagNames(p.value),
    },
    {
      field: "userCategory",
      headerName: "Type",
      flex: 1.2,
      renderCell: (p) => renderDataTagNames(p.value),
    },

    ...(!isDataBase
      ? [
          {
            field: "sensitivityData",
            headerName: "Sensitivity",
            headerAlign: "center",
            align: "center",
            flex: 2.8,
            renderCell: (p) => (
              <div>
                <SensitivityBadge
                  parentprops={p.value}
                  loc="DataTagUser"
                  title=""
                />
              </div>
            ),
          },
          {
            field: "fieldSize",
            headerName: "Files Size",
            flex: 0.7,
            headerAlign: "center",
            align: "center",
            renderCell: (p) => safe(p.value),
            sortComparator: (v1, v2) => sizeToBytes(v1) - sizeToBytes(v2),
          },
          {
            field: "noOfPdaFiles",
            headerName: "PDA Files",
            headerAlign: "center",
            align: "center",
            flex: 1,
            renderCell: (p) => safe(p.value),
          },
          {
            field: "noOfFiles",
            headerName: "Total Files",
            headerAlign: "center",
            align: "center",
            flex: 1,
            renderCell: (p) => safe(p.value),
          },
        ]
      : []),
  ];

  return (
    <div className="dt-all-files-table">
      <div className="quick-filter-bar">
        <span className="quick-filter-title">Sensitivity:</span>
        <DataTagSensitivityFilter
          value={selectedSensitivity}
          onChange={setSelectedSensitivity}
          loc="UsersTable"
        />
      </div>
      <DataGrid
        rows={tableData.map((r, index) => ({ id: index + 1, ...r }))}
        columns={columns}
        className="mui-table"
        rowCount={totalCount}
        pagination
        paginationMode="server"
        paginationModel={paginationModel}
        onPaginationModelChange={handlePaginationChange}
        onRowClick={({ row }) => handleSelect(row)}
        pageSizeOptions={[5, 10, 20, 50, 100]}
        sx={{
          "& .MuiDataGrid-virtualScroller": {
            overflowX: "hidden",
            cursor: "pointer",
          },
        }}
      />
      <DriveDrawer
        open={drawerOpen}
        onClose={handleCloseDrawer}
        drive={activeDrive}
        dupInfo={
          activeDrive
            ? (dupLookup[activeDrive.enterpriseActorId] ?? null)
            : null
        }
        activeDrive={activeDrive}
        loc="usersTable"
      />
    </div>
  );
}
